import { html, nothing, type TemplateResult } from "lit";
import type {
  ModelPricingEntry,
  InferenceConfigState,
} from "../controllers/inference-config.ts";
import { inferenceDirtyKey } from "../controllers/inference-config.ts";

export type BillingPlanEntry = {
  id: string;
  name: string;
  maintenanceFeeCents: number;
  includedTokens: number;
  overagePerMillionCents: number;
  isSubscription: boolean;
};

export type InferenceConfigProps = {
  loading: boolean;
  saving: boolean;
  entries: ModelPricingEntry[];
  dirty: Set<string>;
  error: string | null;
  onUpdate: (
    provider: string,
    modelId: string,
    field: "input" | "output" | "cacheRead" | "cacheWrite",
    value: number,
  ) => void;
  onSave: () => void;
  onRefresh: () => void;
  // Billing fields
  maintenanceFeeCents: number;
  selectedPlanId: string | null;
  extraPerToken: boolean;
  plans: BillingPlanEntry[];
  billingDirty: boolean;
  onMaintenanceFeeChange: (cents: number) => void;
  onPlanChange: (planId: string) => void;
  onExtraPerTokenToggle: (enabled: boolean) => void;
  onBillingSave: () => void;
};

const COST_FIELDS: Array<{
  key: keyof ModelPricingEntry["cost"];
  label: string;
}> = [
  { key: "input", label: "Input $/M" },
  { key: "output", label: "Output $/M" },
  { key: "cacheRead", label: "Cache Read $/M" },
  { key: "cacheWrite", label: "Cache Write $/M" },
];

function renderProviderGroup(
  provider: string,
  models: ModelPricingEntry[],
  props: InferenceConfigProps,
): TemplateResult {
  const dirtyCount = models.filter((m) =>
    props.dirty.has(inferenceDirtyKey(m.provider, m.modelId)),
  ).length;

  return html`
    <div class="card" style="margin-bottom: 16px;">
      <div class="row" style="justify-content: space-between; align-items: center; margin-bottom: 12px;">
        <div>
          <div class="card-title" style="font-size: 0.9em;">${provider}</div>
          <div class="card-sub" style="font-size: 0.75em;">${models.length} model${models.length === 1 ? "" : "s"}</div>
        </div>
        ${dirtyCount > 0
          ? html`<button
              class="btn btn--sm primary"
              ?disabled=${props.saving}
              @click=${props.onSave}
            >
              ${props.saving ? "Saving…" : `Save (${dirtyCount})`}
            </button>`
          : nothing
        }
      </div>

      <table class="settings-table" style="font-size: 0.85em;">
        <thead>
          <tr>
            <th style="text-align: left;">Model</th>
            ${COST_FIELDS.map((f) => html`<th style="text-align: right;">${f.label}</th>`)}
          </tr>
        </thead>
        <tbody>
          ${models.map((m) => {
            const isDirty = props.dirty.has(inferenceDirtyKey(m.provider, m.modelId));
            return html`
              <tr style="${isDirty ? "background: var(--glass-border, rgba(255,255,255,0.05));" : ""}">
                <td style="padding: 4px 8px;">
                  <div>${m.name}</div>
                  <code class="muted" style="font-size: 0.75em;">${m.modelId}</code>
                </td>
                ${COST_FIELDS.map((f) => html`
                  <td style="padding: 2px 8px; text-align: right;">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      .value=${String(m.cost[f.key])}
                      style="width: 70px; text-align: right; font-size: 0.85em; padding: 2px 4px;"
                      @change=${(e: Event) => {
                        const val = parseFloat((e.target as HTMLInputElement).value);
                        if (!isNaN(val) && val >= 0) {
                          props.onUpdate(m.provider, m.modelId, f.key, val);
                        }
                      }}
                    />
                  </td>
                `)}
              </tr>
            `;
          })}
        </tbody>
      </table>
    </div>
  `;
}

export function renderInferenceConfig(props: InferenceConfigProps) {
  const providers = new Map<string, ModelPricingEntry[]>();
  for (const entry of props.entries) {
    if (!providers.has(entry.provider)) {
      providers.set(entry.provider, []);
    }
    providers.get(entry.provider)!.push(entry);
  }
  const providerNames = Array.from(providers.keys()).sort();
  const totalDirty = props.dirty.size;

  return html`
    <div class="card" style="margin-bottom: 16px;">
      <div class="row" style="justify-content: space-between; align-items: center;">
        <div>
          <div class="card-title">Inference Config</div>
          <div class="card-sub">
            Set cost per million tokens (USD) for each model. Overview cost summaries use these values.
          </div>
        </div>
        <div class="row" style="gap: 8px;">
          <button class="btn" ?disabled=${props.loading} @click=${props.onRefresh}>
            ${props.loading ? "Loading…" : "Refresh"}
          </button>
          <button
            class="btn primary"
            ?disabled=${props.saving || totalDirty === 0}
            @click=${props.onSave}
          >
            ${props.saving ? "Saving…" : `Save All${totalDirty > 0 ? ` (${totalDirty})` : ""}`}
          </button>
        </div>
      </div>

      ${props.error
        ? html`<div class="callout danger" style="margin-top: 12px;">${props.error}</div>`
        : nothing}

      <div class="callout info" style="margin-top: 12px;">
        All prices are per 1 million tokens in USD. E.g. <code>3.00</code> = $3.00 per million tokens.
        Set to <code>0</code> for free or untracked models.
      </div>
    </div>

    <!-- Billing Settings -->
    <div class="card" style="margin-bottom: 16px;">
      <div class="row" style="justify-content: space-between; align-items: center; margin-bottom: 12px;">
        <div>
          <div class="card-title" style="font-size: 0.9em;">Billing Settings</div>
          <div class="card-sub" style="font-size: 0.75em;">Maintenance fee, subscription plan, and per-token billing options.</div>
        </div>
        <button
          class="btn btn--sm primary"
          ?disabled=${props.saving || !props.billingDirty}
          @click=${props.onBillingSave}
        >
          ${props.saving ? "Saving…" : "Save Billing"}
        </button>
      </div>

      <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; align-items: start;">
        <label class="field">
          <span>Maintenance Fee (€/month)</span>
          <input
            type="number"
            min="0"
            step="1"
            .value=${String((props.maintenanceFeeCents / 100).toFixed(2))}
            @change=${(e: Event) => {
              const val = parseFloat((e.target as HTMLInputElement).value);
              if (!isNaN(val) && val >= 0) {
                props.onMaintenanceFeeChange(Math.round(val * 100));
              }
            }}
          />
        </label>

        <label class="field">
          <span>Subscription Plan</span>
          <select
            .value=${props.selectedPlanId ?? ""}
            @change=${(e: Event) => props.onPlanChange((e.target as HTMLSelectElement).value)}
          >
            <option value="">— None (Pay-as-you-go) —</option>
            ${props.plans.map((p) => html`<option value=${p.id} ?selected=${props.selectedPlanId === p.id}>
              ${p.name} (${(p.maintenanceFeeCents / 100).toFixed(0)}€/mo${p.isSubscription ? `, ${p.includedTokens > 0 ? `${(p.includedTokens / 1_000_000).toFixed(0)}M tokens included` : "no tokens"}` : ""})
            </option>`)}
          </select>
        </label>

        <label class="field" style="display: flex; flex-direction: column; gap: 4px;">
          <span>Extra per token</span>
          <label style="display: flex; align-items: center; gap: 6px; font-size: 0.85em; cursor: pointer;">
            <input
              type="checkbox"
              .checked=${props.extraPerToken}
              @change=${(e: Event) => props.onExtraPerTokenToggle((e.target as HTMLInputElement).checked)}
            />
            <span>Add plan overage rate on top of inference pricing</span>
          </label>
        </label>
      </div>

      ${props.selectedPlanId
        ? (() => {
            const plan = props.plans.find((p) => p.id === props.selectedPlanId);
            if (!plan) return nothing;
            return html`<div class="callout" style="margin-top: 12px; font-size: 0.85em;">
              <strong>${plan.name}</strong>: €${(plan.maintenanceFeeCents / 100).toFixed(2)}/mo maintenance
              ${plan.isSubscription
                ? html` · ${(plan.includedTokens / 1_000_000).toFixed(0)}M tokens included
                   · Overage: €${(plan.overagePerMillionCents / 100).toFixed(2)}/M tokens`
                : html` · Usage billed at inference pricing`}
              ${props.extraPerToken
                ? html` · <strong>+ extra per token enabled</strong>`
                : nothing}
            </div>`;
          })()
        : nothing
      }
    </div>

    ${props.loading && props.entries.length === 0
      ? html`<div class="muted" style="padding: 16px;">Loading model pricing…</div>`
      : nothing}

    ${!props.loading && props.entries.length === 0
      ? html`<div class="muted" style="padding: 16px;">No models found. Add providers in the Models tab first.</div>`
      : nothing}

    ${providerNames.map((name) =>
      renderProviderGroup(name, providers.get(name)!, props),
    )}

    ${props.entries.length > 0
      ? html`<p class="muted" style="margin-top: 0.5rem;">
          ${props.entries.length} models across ${providerNames.length} provider${providerNames.length === 1 ? "" : "s"}
          ${totalDirty > 0 ? html` · <strong>${totalDirty} unsaved change${totalDirty === 1 ? "" : "s"}</strong>` : nothing}
        </p>`
      : nothing
    }
  `;
}

export type { InferenceConfigState };
